// client/src/coms/interactions/InteractionComposer.js
// One dialog creates every interaction type for a channel (text channel or voice-channel chat). Opened from
// the channel context menu, the composer's interaction button, a message's "Make poll / suggestion / task"
// action, or the mobile long-press menu, through InteractionHost. Types the member may not create are shown
// disabled; the server checks every permission again.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { comsApiGet } from '../comsApi';
import { TYPE_META, TYPE_ORDER, errorText, interactionApi, useInteractionCapabilities } from './interactionStore';
import './interactions.css';

const DURATIONS = [
  ['0', 'No time limit'],
  ['60', '1 hour'],
  ['240', '4 hours'],
  ['1440', '1 day'],
  ['4320', '3 days'],
  ['10080', '1 week'],
  ['custom', 'Pick a date and time…'],
];
const REMINDER_CHOICES = [
  [1440, '1 day before'],
  [60, '1 hour before'],
  [15, '15 minutes before'],
  [0, 'At the time'],
];

function zone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function localInput(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nextHour(offsetHours = 1) {
  const d = new Date(Date.now() + offsetHours * 3600000);
  d.setMinutes(0, 0, 0);
  return d.getTime();
}

const toMs = (v) => (v ? new Date(v).getTime() : 0);

function RolePicker({ roles, value, onChange, label }) {
  if (!roles || !roles.length) return null;
  return (
    <fieldset className="vxi-field" style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
      <legend className="vxi-label" style={{ marginBottom: 4 }}>{label}</legend>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {roles.map((r) => {
          const on = value.includes(r.id);
          return (
            <button key={r.id} type="button" className={`vxi-btn small${on ? ' active' : ''}`} aria-pressed={on} onClick={() => onChange(on ? value.filter((x) => x !== r.id) : [...value, r.id])}>
              {r.color ? <span className="vxi-swatch" style={{ background: r.color }} aria-hidden="true" /> : null}
              {r.name}
            </button>
          );
        })}
      </div>
      <span className="vxi-hint">{value.length ? 'Only members with one of these roles can vote.' : 'Everyone who can vote here.'}</span>
    </fieldset>
  );
}

function Reminders({ value, onChange }) {
  return (
    <fieldset className="vxi-field" style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
      <legend className="vxi-label" style={{ marginBottom: 4 }}>Reminders (push notifications to interested members)</legend>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {REMINDER_CHOICES.map(([m, l]) => {
          const on = value.includes(m);
          return (
            <button key={m} type="button" className={`vxi-btn small${on ? ' active' : ''}`} aria-pressed={on} onClick={() => onChange(on ? value.filter((x) => x !== m) : [...value, m])}>
              {l}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function VoicePicker({ voice, value, onChange }) {
  if (!voice || !voice.length) return null;
  return (
    <label className="vxi-field">
      <span>Linked voice channel</span>
      <select value={value || ''} onChange={(e) => onChange(Number(e.target.value) || 0)}>
        <option value="">None</option>
        {voice.map((v) => (
          <option key={v.id} value={v.id}>🔊 {v.name}</option>
        ))}
      </select>
      <span className="vxi-hint">Shows a Join voice button when it starts. Nobody is moved into voice automatically.</span>
    </label>
  );
}

export default function InteractionComposer({ detail, onClose }) {
  const { serverKey, kind, channelId, channelName } = detail;
  const caps = useInteractionCapabilities(kind, channelId);
  const prefill = detail.prefill || {};
  const [type, setType] = useState(detail.type || 'poll');
  const [title, setTitle] = useState(prefill.title || '');
  const [description, setDescription] = useState(prefill.description || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [roles, setRoles] = useState([]);
  const [voice, setVoice] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [pin, setPin] = useState(false);
  // poll
  const [choices, setChoices] = useState([{ label: '', emoji: '', color: '' }, { label: '', emoji: '', color: '' }]);
  const [mode, setMode] = useState('single');
  const [maxSel, setMaxSel] = useState(2);
  const [anonymous, setAnonymous] = useState(false);
  const [allowChange, setAllowChange] = useState(true);
  const [results, setResults] = useState('live');
  const [duration, setDuration] = useState('1440');
  const [closesAt, setClosesAt] = useState(localInput(nextHour(24)));
  const [voterRoles, setVoterRoles] = useState([]);
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  // event
  const [slots, setSlots] = useState([localInput(nextHour(24)), localInput(nextHour(48))]);
  const [eventMinutes, setEventMinutes] = useState(60);
  const [maxAttendees, setMaxAttendees] = useState('');
  const [voiceId, setVoiceId] = useState(kind === 'voice' ? Number(channelId) : 0);
  const [reminders, setReminders] = useState([15]);
  const [repeat, setRepeat] = useState('none');
  const [repeatCount, setRepeatCount] = useState(4);
  const [location, setLocation] = useState('');
  const [voteDeadline, setVoteDeadline] = useState('');
  // decision
  const [approval, setApproval] = useState(50);
  const [quorum, setQuorum] = useState(0);
  const [decisionLive, setDecisionLive] = useState(false);
  // tasks
  const [taskLines, setTaskLines] = useState(prefill.taskLines || '');
  const [openCompletion, setOpenCompletion] = useState(true);
  const [notifyComplete, setNotifyComplete] = useState(false);
  // qa
  const [allowAnon, setAllowAnon] = useState(true);
  // countdown
  const [target, setTarget] = useState(localInput(nextHour(24)));
  const [cdReminders, setCdReminders] = useState([60, 0]);
  const firstRef = useRef(null);

  useEffect(() => {
    let alive = true;
    comsApiGet(`/servers/${encodeURIComponent(serverKey)}/roles`)
      .then((r) => alive && setRoles(((r && r.roles) || []).filter((x) => !Number(x.is_owner)).map((x) => ({ id: Number(x.id), name: x.name, color: x.color }))))
      .catch(() => {});
    comsApiGet(`/servers/${encodeURIComponent(serverKey)}/channels`)
      .then((r) => alive && setVoice(((r && r.voice_channels) || []).map((v) => ({ id: Number(v.id), name: v.name }))))
      .catch(() => {});
    interactionApi
      .templates(serverKey)
      .then((r) => alive && setTemplates((r && r.templates) || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [serverKey]);

  useEffect(() => {
    if (firstRef.current) firstRef.current.focus();
  }, [type]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // A type the member cannot create: switch to the first one they can.
  useEffect(() => {
    if (!caps || !caps.create) return;
    if (caps.create[type] === false) {
      const first = TYPE_ORDER.find((t) => caps.create[t]);
      if (first) setType(first);
    }
  }, [caps, type]);

  const allowed = (t) => !caps || !caps.create || caps.create[t] !== false;
  const nothingAllowed = caps && caps.create && !TYPE_ORDER.some((t) => caps.create[t]);

  function applyTemplate(id) {
    const t = templates.find((x) => String(x.id) === String(id));
    if (!t) return;
    const c = t.config || {};
    if (c.title) setTitle(c.title);
    if (c.description) setDescription(c.description);
    if (Array.isArray(c.options) && c.options.length >= 2) setChoices(c.options.map((o) => ({ label: o.label || '', emoji: o.emoji || '', color: o.color || '' })));
    setMode(c.mode || 'single');
    if (c.max_selections) setMaxSel(c.max_selections);
    setAnonymous(!!c.anonymous);
    setAllowChange(c.allow_vote_change !== false);
    setResults(c.results || 'live');
    setDuration(c.duration_minutes ? String(c.duration_minutes) : '0');
  }

  const body = useMemo(() => {
    const b = { type, title: title.trim(), description: description.trim(), pin, source_message_id: prefill.source_message_id || undefined };
    if (type === 'poll') {
      b.options = choices.filter((c) => c.label.trim()).map((c) => ({ label: c.label.trim(), emoji: c.emoji.trim() || undefined, color: c.color || undefined }));
      b.config = {
        mode,
        max_selections: mode === 'multiple' ? Number(maxSel) || 2 : undefined,
        anonymous,
        allow_vote_change: allowChange,
        results,
        voter_role_ids: voterRoles,
        ...(duration === 'custom' ? { closes_at_ms: toMs(closesAt) } : { duration_minutes: Number(duration) || 0 }),
      };
    } else if (type === 'event') {
      b.options = slots.map(toMs).filter(Boolean).map((ms) => ({ starts_at_ms: ms }));
      b.config = { timezone: zone(), duration_minutes: Number(eventMinutes) || 60, max_attendees: Number(maxAttendees) || 0, voice_channel_id: voiceId || 0, reminders, repeat, repeat_count: Number(repeatCount) || 1, location: location.trim(), vote_deadline_ms: toMs(voteDeadline) || 0 };
    } else if (type === 'decision') {
      b.config = { approval_pct: Number(approval) || 50, quorum: Number(quorum) || 0, anonymous, allow_vote_change: allowChange, results: decisionLive ? 'live' : 'after_close', voter_role_ids: voterRoles, closes_at_ms: duration === 'custom' ? toMs(closesAt) : Number(duration) ? Date.now() + Number(duration) * 60000 : 0 };
    } else if (type === 'tasks') {
      b.items = taskLines
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .slice(0, 100)
        .map((l, i) => ({ body: l, source_message_id: i === 0 && prefill.taskSourceMessageId ? prefill.taskSourceMessageId : undefined }));
      b.config = { open_completion: openCompletion, notify_on_complete: notifyComplete };
    } else if (type === 'qa') {
      b.config = { allow_anonymous: allowAnon, closes_at_ms: duration === 'custom' ? toMs(closesAt) : Number(duration) ? Date.now() + Number(duration) * 60000 : 0 };
    } else if (type === 'countdown') {
      b.config = { target_ms: toMs(target), reminders: cdReminders, voice_channel_id: voiceId || 0 };
    }
    return b;
  }, [type, title, description, pin, prefill.source_message_id, prefill.taskSourceMessageId, choices, mode, maxSel, anonymous, allowChange, results, voterRoles, duration, closesAt, slots, eventMinutes, maxAttendees, voiceId, reminders, repeat, repeatCount, location, voteDeadline, approval, quorum, decisionLive, taskLines, openCompletion, notifyComplete, allowAnon, target, cdReminders]);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      if (type === 'poll' && saveTemplate && templateName.trim()) {
        await interactionApi.saveTemplate(serverKey, { name: templateName.trim(), config: { title: body.title, description: body.description, options: body.options, mode, max_selections: maxSel, anonymous, allow_vote_change: allowChange, results, duration_minutes: duration === 'custom' ? 0 : Number(duration) || 0 } }).catch(() => {});
      }
      await interactionApi.create(kind, channelId, body);
      onClose();
    } catch (ex) {
      setErr(errorText(ex, 'Could not create it'));
    } finally {
      setBusy(false);
    }
  }

  const durationField = (label) => (
    <div className="vxi-grid2">
      <label className="vxi-field">
        <span>{label}</span>
        <select value={duration} onChange={(e) => setDuration(e.target.value)}>
          {DURATIONS.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      </label>
      {duration === 'custom' ? (
        <label className="vxi-field">
          <span>Closes at</span>
          <input type="datetime-local" value={closesAt} onChange={(e) => setCloses(e.target.value)} />
        </label>
      ) : null}
    </div>
  );
  function setCloses(v) {
    setClosesAt(v);
  }

  const meta = TYPE_META[type];
  return (
    <div className="vxi-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <form className="vxi-dialog" role="dialog" aria-modal="true" aria-labelledby="vxi-composer-title" onSubmit={submit}>
        <div className="vxi-dialog-head">
          <h2 id="vxi-composer-title">
            {meta.icon} New {meta.label.toLowerCase()}
            <div className="vxi-hint" style={{ fontWeight: 600 }}>
              in {kind === 'voice' ? '🔊' : '#'}
              {channelName || 'this channel'}
              {kind === 'voice' ? ' (voice chat)' : ''}
            </div>
          </h2>
          <button type="button" className="vxi-btn ghost" aria-label="Close" onClick={onClose} disabled={busy}>
            ✕
          </button>
        </div>
        <div className="vxi-tabs" role="tablist" aria-label="Interaction type">
          {TYPE_ORDER.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={type === t} disabled={!allowed(t)} title={allowed(t) ? TYPE_META[t].hint : 'You do not have permission to create this here'} onClick={() => setType(t)}>
              {TYPE_META[t].icon} {TYPE_META[t].label}
            </button>
          ))}
        </div>
        <div className="vxi-dialog-body">
          {nothingAllowed ? <div className="vxi-error">You cannot create interactions in this channel.</div> : null}
          {type === 'poll' && templates.length ? (
            <label className="vxi-field">
              <span>Start from a template</span>
              <select defaultValue="" onChange={(e) => applyTemplate(e.target.value)}>
                <option value="">—</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="vxi-field">
            <span>{type === 'poll' ? 'Question' : type === 'decision' ? 'Proposal' : type === 'suggestion' ? 'Suggestion' : 'Title'}</span>
            <input ref={firstRef} type="text" value={title} maxLength={type === 'suggestion' ? 150 : 200} required onChange={(e) => setTitle(e.target.value)} placeholder={type === 'poll' ? 'What should we play tonight?' : type === 'countdown' ? 'Season launch' : ''} />
          </label>
          <label className="vxi-field">
            <span>Description (optional)</span>
            <textarea value={description} maxLength={2000} rows={2} onChange={(e) => setDescription(e.target.value)} />
          </label>
          {prefill.source_message_id || prefill.taskSourceMessageId ? <div className="vxi-hint" style={{ marginBottom: 10 }}>Made from a message by {prefill.sourceAuthor || 'a member'} — the message stays as it is.</div> : null}

          {type === 'poll' ? (
            <>
              <div className="vxi-label" style={{ marginBottom: 6 }}>Choices</div>
              {choices.map((c, i) => (
                <div key={i} className="vxi-choice-row">
                  <input type="text" className="vxi-emoji" value={c.emoji} maxLength={8} aria-label={`Emoji for choice ${i + 1}`} placeholder="🙂" onChange={(e) => setChoices(choices.map((x, j) => (j === i ? { ...x, emoji: e.target.value } : x)))} />
                  <input type="text" value={c.label} maxLength={100} aria-label={`Choice ${i + 1}`} placeholder={`Choice ${i + 1}`} onChange={(e) => setChoices(choices.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                  <input type="color" value={c.color || '#5865f2'} aria-label={`Colour for choice ${i + 1}`} onChange={(e) => setChoices(choices.map((x, j) => (j === i ? { ...x, color: e.target.value } : x)))} />
                  <button type="button" className="vxi-btn small ghost" aria-label={`Remove choice ${i + 1}`} disabled={choices.length <= 2} onClick={() => setChoices(choices.filter((_, j) => j !== i))}>
                    ✕
                  </button>
                </div>
              ))}
              <button type="button" className="vxi-btn small" disabled={choices.length >= 12} onClick={() => setChoices([...choices, { label: '', emoji: '', color: '' }])} style={{ marginBottom: 12 }}>
                + Add choice ({choices.length}/12)
              </button>
              <div className="vxi-grid2">
                <label className="vxi-field">
                  <span>Voting</span>
                  <select value={mode} onChange={(e) => setMode(e.target.value)}>
                    <option value="single">One choice</option>
                    <option value="multiple">Several choices</option>
                    <option value="ranked">Ranked choice (instant runoff)</option>
                  </select>
                </label>
                {mode === 'multiple' ? (
                  <label className="vxi-field">
                    <span>Maximum choices</span>
                    <input type="number" min={1} max={choices.length} value={maxSel} onChange={(e) => setMaxSel(e.target.value)} />
                  </label>
                ) : (
                  <label className="vxi-field">
                    <span>Results</span>
                    <select value={results} onChange={(e) => setResults(e.target.value)}>
                      <option value="live">Live while voting</option>
                      <option value="after_close">Hidden until it closes</option>
                    </select>
                  </label>
                )}
              </div>
              {mode === 'multiple' ? (
                <label className="vxi-field">
                  <span>Results</span>
                  <select value={results} onChange={(e) => setResults(e.target.value)}>
                    <option value="live">Live while voting</option>
                    <option value="after_close">Hidden until it closes</option>
                  </select>
                </label>
              ) : null}
              {mode === 'ranked' ? <div className="vxi-hint" style={{ marginBottom: 10 }}>Voters rank choices. The lowest choice is eliminated round by round until one has more than half; ties eliminate the choice weaker in earlier rounds, then the one listed later.</div> : null}
              {durationField('Closes')}
              <label className="vxi-toggle"><input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} /> Anonymous voting (nobody, not even moderators, can see who voted for what)</label>
              <label className="vxi-toggle"><input type="checkbox" checked={allowChange} onChange={(e) => setAllowChange(e.target.checked)} /> Voters can change or remove their vote</label>
              <RolePicker roles={roles} value={voterRoles} onChange={setVoterRoles} label="Who can vote" />
              <label className="vxi-toggle"><input type="checkbox" checked={saveTemplate} onChange={(e) => setSaveTemplate(e.target.checked)} /> Save as a template for this server</label>
              {saveTemplate ? (
                <label className="vxi-field">
                  <span>Template name</span>
                  <input type="text" value={templateName} maxLength={60} onChange={(e) => setTemplateName(e.target.value)} />
                </label>
              ) : null}
            </>
          ) : null}

          {type === 'event' ? (
            <>
              <div className="vxi-label" style={{ marginBottom: 6 }}>Proposed times (your time zone: {zone()})</div>
              {slots.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input type="datetime-local" className="vxi-input" value={s} aria-label={`Proposed time ${i + 1}`} onChange={(e) => setSlots(slots.map((x, j) => (j === i ? e.target.value : x)))} />
                  <button type="button" className="vxi-btn small ghost" aria-label={`Remove time ${i + 1}`} disabled={slots.length <= 1} onClick={() => setSlots(slots.filter((_, j) => j !== i))}>✕</button>
                </div>
              ))}
              <button type="button" className="vxi-btn small" disabled={slots.length >= 10} onClick={() => setSlots([...slots, localInput(nextHour(24 * (slots.length + 1)))])} style={{ marginBottom: 6 }}>
                + Add a time
              </button>
              <div className="vxi-hint" style={{ marginBottom: 12 }}>With one time the event is confirmed straight away; with several, members vote on their availability and you confirm the final time.</div>
              <div className="vxi-grid2">
                <label className="vxi-field">
                  <span>Length (minutes)</span>
                  <input type="number" min={5} max={20160} value={eventMinutes} onChange={(e) => setEventMinutes(e.target.value)} />
                </label>
                <label className="vxi-field">
                  <span>Maximum attendees (optional)</span>
                  <input type="number" min={0} value={maxAttendees} onChange={(e) => setMaxAttendees(e.target.value)} placeholder="No limit" />
                </label>
              </div>
              <VoicePicker voice={voice} value={voiceId} onChange={setVoiceId} />
              <Reminders value={reminders} onChange={setReminders} />
              <div className="vxi-grid2">
                <label className="vxi-field">
                  <span>Repeat</span>
                  <select value={repeat} onChange={(e) => setRepeat(e.target.value)}>
                    <option value="none">Does not repeat</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </label>
                {repeat !== 'none' ? (
                  <label className="vxi-field">
                    <span>Number of occurrences</span>
                    <input type="number" min={2} max={52} value={repeatCount} onChange={(e) => setRepeatCount(e.target.value)} />
                  </label>
                ) : (
                  <label className="vxi-field">
                    <span>Location (optional)</span>
                    <input type="text" value={location} maxLength={120} onChange={(e) => setLocation(e.target.value)} />
                  </label>
                )}
              </div>
              {slots.length > 1 ? (
                <label className="vxi-field">
                  <span>Stop time voting at (optional)</span>
                  <input type="datetime-local" value={voteDeadline} onChange={(e) => setVoteDeadline(e.target.value)} />
                </label>
              ) : null}
            </>
          ) : null}

          {type === 'decision' ? (
            <>
              <div className="vxi-grid2">
                <label className="vxi-field">
                  <span>Approval needs Yes of Yes + No</span>
                  <select value={approval} onChange={(e) => setApproval(e.target.value)}>
                    <option value={50}>Simple majority (more than half)</option>
                    <option value={60}>60%</option>
                    <option value={66}>Two thirds (66%)</option>
                    <option value={75}>75%</option>
                    <option value={100}>Unanimous</option>
                  </select>
                </label>
                <label className="vxi-field">
                  <span>Quorum (minimum votes, 0 = none)</span>
                  <input type="number" min={0} value={quorum} onChange={(e) => setQuorum(e.target.value)} />
                </label>
              </div>
              <div className="vxi-hint" style={{ marginBottom: 10 }}>Abstentions count towards the quorum but not for or against. A tie never passes. Without enough votes the outcome is No quorum.</div>
              {durationField('Voting deadline')}
              <label className="vxi-toggle"><input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} /> Private ballots</label>
              <label className="vxi-toggle"><input type="checkbox" checked={allowChange} onChange={(e) => setAllowChange(e.target.checked)} /> Voters can change their vote until it closes</label>
              <label className="vxi-toggle"><input type="checkbox" checked={decisionLive} onChange={(e) => setDecisionLive(e.target.checked)} /> Show the tally while voting (otherwise only once closed)</label>
              <RolePicker roles={roles} value={voterRoles} onChange={setVoterRoles} label="Eligible voters" />
            </>
          ) : null}

          {type === 'tasks' ? (
            <>
              <label className="vxi-field">
                <span>Tasks (one per line)</span>
                <textarea value={taskLines} rows={5} onChange={(e) => setTaskLines(e.target.value)} placeholder={'Book the venue\nSend invites'} />
                <span className="vxi-hint">Assign people and due dates on the card afterwards.</span>
              </label>
              <label className="vxi-toggle"><input type="checkbox" checked={openCompletion} onChange={(e) => setOpenCompletion(e.target.checked)} /> Members can tick off unassigned tasks</label>
              <label className="vxi-toggle"><input type="checkbox" checked={notifyComplete} onChange={(e) => setNotifyComplete(e.target.checked)} /> Notify me when tasks are completed</label>
            </>
          ) : null}

          {type === 'qa' ? (
            <>
              <label className="vxi-toggle"><input type="checkbox" checked={allowAnon} onChange={(e) => setAllowAnon(e.target.checked)} /> Allow anonymous questions</label>
              {durationField('Closes')}
            </>
          ) : null}

          {type === 'countdown' ? (
            <>
              <label className="vxi-field">
                <span>Target date and time ({zone()})</span>
                <input type="datetime-local" value={target} required onChange={(e) => setTarget(e.target.value)} />
              </label>
              <Reminders value={cdReminders} onChange={setCdReminders} />
              <VoicePicker voice={voice} value={voiceId} onChange={setVoiceId} />
            </>
          ) : null}

          {caps && caps.can_pin ? <label className="vxi-toggle"><input type="checkbox" checked={pin} onChange={(e) => setPin(e.target.checked)} /> Pin it in the channel</label> : null}
          {err ? <div className="vxi-error" role="alert">{err}</div> : null}
        </div>
        <div className="vxi-dialog-foot">
          <button type="button" className="vxi-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="vxi-btn primary" disabled={busy || !title.trim() || nothingAllowed || !allowed(type)} data-testid="interaction-create-submit">
            {busy ? 'Posting…' : `Post ${meta.label.toLowerCase()}`}
          </button>
        </div>
      </form>
    </div>
  );
}
